/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React, { useContext, useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';

import { useGraphqlSchemaContext } from '../GraphqlSchemaContext/GraphqlSchemaContext';
import { useProductPickerContext } from '../ProductPicker/ProductPickerContext';
import { useCategoryPickerContext } from '../CategoryPicker/CategoryPickerContext';
import sendEvent from './sendEvent';
import triggerEvent from './triggerEvent';
import { trackEvent } from '../../utils/tracking';
import { event_categorypicker_select, event_productpicker_select } from '../../utils/trackingEvents';

export const SelectionContext = React.createContext();

const reducer = (state, action) => ({
    ...state,
    ...(typeof action === 'function' ? action(state) : action)
});

const getContextData = (productPickerContext, categoryPickerContext) => {
    if (productPickerContext) {
        const [{ items }, { getProducts: fetchSelectionInfo, getSelectionId }] = productPickerContext;
        return { items, fetchSelectionInfo, getSelectionId };
    } else if (categoryPickerContext) {
        const [{ items }, { getCategories: fetchSelectionInfo, getSelectionId }] = categoryPickerContext;
        return { items, fetchSelectionInfo, getSelectionId };
    } else {
        throw Error('Cannot use SelectionContextProvider outside of CategoryPickerContext or ProductPickerContext');
    }
};

const SelectionContextProvider = props => {
    const { preSelection, preSelectionKey, pickerSelectedData, currentValue, multiple, provideWebComponentApi } = props;

    const [schemaStatus, , [config]] = useGraphqlSchemaContext();
    const productPickerContext = useProductPickerContext();
    const categoryPickerContext = useCategoryPickerContext();
    const [state, setState] = useReducer(reducer, {
        // State that tracks selections in picker
        pickerSelection: new Set(),
        // State that tracks selection in field, stored by key
        fieldSelection: new Map(),
        // Loading state, used when asynchronously fetching data
        loading: false
    });
    const fieldSelectionRef = useRef();
    fieldSelectionRef.current = state.fieldSelection;

    const { items, fetchSelectionInfo, getSelectionId } = getContextData(productPickerContext, categoryPickerContext);

    const setPickerSelection = newSet => {
        trackSelectionEvent();
        if (!newSet) return;

        if (newSet === 'all') {
            newSet = selectAllVisibleItems();
        } else if (newSet.size === 0) {
            newSet = deselectVisibleItems();
        }

        setState({ pickerSelection: newSet });
    };

    const trackSelectionEvent = () => {
        if (productPickerContext) {
            trackEvent(event_productpicker_select);
        } else if (categoryPickerContext) {
            trackEvent(event_categorypicker_select);
        }
    };

    const selectAllVisibleItems = () => {
        return new Set([...state.pickerSelection, ...items.map(i => i.key)]);
    };

    const deselectVisibleItems = () => {
        return new Set([...state.pickerSelection].filter(key => !items.some(i => i.key === key)));
    };

    const sendEvents = (oldFieldSelection, newFieldSelection) => {
        const { target, targetType, webComponentTag, domRoot } = props;
        let value;
        let type;

        // Send events for multiple selections
        if (multiple) {
            // Send add events
            newFieldSelection.forEach((selection, key) => {
                if (!oldFieldSelection.has(key)) {
                    [value, type] = getSelectionId(selection, preSelectionKey, config.supportUID);
                    sendEvent(domRoot, target, 'add', value, webComponentTag);
                }
            });

            // Send remove events
            oldFieldSelection.forEach((selection, key) => {
                if (!newFieldSelection.has(key)) {
                    [value, type] = getSelectionId(selection, preSelectionKey, config.supportUID);
                    sendEvent(domRoot, target, 'remove', value, webComponentTag);
                }
            });
        } else {
            // Send event for single selection
            const selected = newFieldSelection.values().next().value || '';
            if (selected) {
                [value, type] = getSelectionId(selected, preSelectionKey, config.supportUID);
            }

            // If nothing is selected, send event with empty content
            sendEvent(domRoot, target, 'update', value || '', webComponentTag);
            triggerEvent(domRoot, target, value || '', webComponentTag, {
                entityType: 'picker-field',
                prop: 'attribute',
                operation: 'add',
                attributeName: 'current-value',
                apply: !multiple
            });
        }

        // Send type event
        if (type) {
            sendEvent(domRoot, targetType, 'update', type, webComponentTag, false);
        }
    };

    const updatesPickerSelectedData = results => {
        const { target, webComponentTag, domRoot } = props;
        triggerEvent(domRoot, target, JSON.stringify(results), webComponentTag, {
            entityType: 'picker-field',
            prop: 'attribute',
            operation: 'add',
            attributeName: 'picker-selected-data',
            apply: !multiple
        });
    };

    const applyPickerSelection = async () => {
        const newFieldSelection = new Map();
        const results = await fetchSelectionInfo(Array.from(state.pickerSelection));

        updatesPickerSelectedData(results);

        results.forEach(result => {
            newFieldSelection.set(result.key, result);
        });

        sendEvents(state.fieldSelection, newFieldSelection);

        setState({ fieldSelection: newFieldSelection });
    };

    const resetPickerSelection = () => {
        setState(currentState => {
            const newPickerSelection = new Set(currentState.fieldSelection.keys());
            return {
                ...currentState,
                pickerSelection: newPickerSelection
            };
        });
    };

    const addToFieldSelection = async identifiers => {
        const newFieldSelection = multiple ? new Map(state.fieldSelection) : new Map();
        const newPickerSelection = new Set(state.pickerSelection);
        let results;

        // Enter loading state
        setState({ loading: true });

        // Query preselected objects
        if (
            multiple ||
            !pickerSelectedData ||
            (!multiple && pickerSelectedData && pickerSelectedData[0]['key'] !== identifiers[0])
        ) {
            results = await fetchSelectionInfo(identifiers);
        } else {
            results = pickerSelectedData;
        }

        results.forEach(result => {
            newFieldSelection.set(result.key, result);
            newPickerSelection.add(result.key);
        });

        sendEvents(state.fieldSelection, newFieldSelection);
        updatesPickerSelectedData(results);

        // Store them in the state and exit loading state
        setState({
            pickerSelection: newPickerSelection,
            fieldSelection: newFieldSelection,
            loading: false
        });
    };

    const removeFromFieldSelection = identifier => {
        // Copy selection map and remove given identifier
        const newFieldSelection = new Map(state.fieldSelection);
        newFieldSelection.delete(identifier);

        // Rebuild picker selection set based on new field selection
        const newPickerSelection = new Set();
        newFieldSelection.forEach(item => newPickerSelection.add(item.key));

        sendEvents(state.fieldSelection, newFieldSelection);

        setState({
            fieldSelection: newFieldSelection,
            pickerSelection: newPickerSelection
        });
    };

    const clearFieldSelection = () => {
        const newFieldSelection = new Map();
        const newPickerSelection = new Set();

        sendEvents(state.fieldSelection, newFieldSelection);

        setState({
            fieldSelection: newFieldSelection,
            pickerSelection: newPickerSelection
        });
    };

    const setFieldSelection = async identifiers => {
        const newFieldSelection = new Map();
        const newPickerSelection = new Set();

        // Enter loading state
        setState({
            loading: true
        });

        // Filter empty values
        identifiers = identifiers.filter(identifier => identifier && identifier.length > 0);

        // Only take first element if not multiple selection
        if (!multiple && identifiers.length > 1) {
            identifiers = identifiers.slice(0, 1);
        }

        // Query identifiers
        const results = await fetchSelectionInfo(identifiers);
        results.forEach(result => {
            newFieldSelection.set(result.key, result);
            newPickerSelection.add(result.key);
        });

        sendEvents(state.fieldSelection, newFieldSelection);

        // Store them in the state and exit loading state
        setState({
            pickerSelection: newPickerSelection,
            fieldSelection: newFieldSelection,
            loading: false
        });
    };

    const buildWebComponentApi = () => {
        return {
            clear: clearFieldSelection,
            setValue: value => {
                return setFieldSelection([value]);
            },
            setValues: values => {
                return setFieldSelection(values);
            }
        };
    };

    useEffect(() => {
        (async () => {
            // Wait for GraphqlSchemaContext to be ready
            if (schemaStatus !== 'done') {
                return;
            }

            // To resolve the drag & drop issue
            let modifyPreSelection = preSelection;

            if (!multiple && currentValue) {
                modifyPreSelection = currentValue;
            }

            if (modifyPreSelection) {
                // Get preselected values
                const values = multiple ? modifyPreSelection.split(',') : [modifyPreSelection];

                // Add preselected values to current selection
                await addToFieldSelection(values);
            }

            // Call provideWebComponentApi if requested
            if (provideWebComponentApi) {
                provideWebComponentApi(buildWebComponentApi());
            }
        })();
    }, [preSelection, schemaStatus]);

    const api = [
        {
            pickerSelection: state.pickerSelection,
            fieldSelection: Array.from(state.fieldSelection.values()),
            loading: state.loading
        },
        {
            setPickerSelection,
            applyPickerSelection,
            resetPickerSelection,
            addToFieldSelection,
            removeFromFieldSelection
        }
    ];

    return <SelectionContext.Provider value={api}>{props.children}</SelectionContext.Provider>;
};

SelectionContextProvider.propTypes = {
    // Set of keys that should be preselected
    preSelection: PropTypes.string,

    // Key that should be used for preSelection, usually sku or id/uid
    preSelectionKey: PropTypes.string,

    // True if more than one element can be selected
    multiple: PropTypes.bool,

    // Selector of DOM element on which vents are triggered for value changes
    target: PropTypes.string,

    // Selector of DOM element on which events are triggered for type changes
    targetType: PropTypes.string,

    // Tag used for the webcomponent, if embedded in one
    webComponentTag: PropTypes.string,

    // React ref to the root DOM element
    domRoot: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),

    // Callback that should be called once the context is ready. Passes an API object for the web component
    provideWebComponentApi: PropTypes.func,

    // Stores current value of picker to resolve issue with drag & drop
    currentValue: PropTypes.string,

    // Stores selected picker data info to avoid graphql call using drag & drop
    pickerSelectedData: PropTypes.arrayOf(PropTypes.object)
};

export default SelectionContextProvider;

export const useSelectionContext = () => useContext(SelectionContext);
